import { CSSProperties, JSX } from 'react';

import Icon from './icon';
import { IconProps } from './types';
import styles from './icon-with-backdrop.module.css';

type Length = number | string;

const toLength = (value: Length | undefined): string | undefined => {
  if (value === undefined) return undefined;
  return typeof value === 'number' ? `${value}px` : value;
};

interface IconWithBackdropProps extends IconProps {
  backdropColor: string;
  /** width of the backdrop. defaults to backdropSize, then 60% of the icon size */
  backdropWidth?: Length;
  /** height of the backdrop. defaults to backdropSize, then 60% of the icon size */
  backdropHeight?: Length;
  /** shorthand to set both backdropWidth and backdropHeight */
  backdropSize?: Length;
  /** horizontal offset from center. positive moves right */
  backdropOffsetX?: Length;
  /** vertical offset from center. positive moves down */
  backdropOffsetY?: Length;
  /** border-radius of the backdrop. defaults to 50% (circle) */
  backdropBorderRadius?: Length;
  wrapperClassName?: string;
}

/**
 * Renders an `<Icon>` with a colored shape painted behind it. Useful when an icon has
 * transparent cutouts that need to read as a specific color (e.g. an outline-style logo
 * that should appear filled on a dark surface) without modifying the SVG itself.
 *
 * The backdrop is a `::before` pseudo-element absolutely positioned behind the icon.
 * Size it smaller than the icon's drawn area so it doesn't bleed past the silhouette,
 * and use the offset props to nudge it if the cutout isn't centered.
 *
 * @example
 * <IconWithBackdrop
 *   name={ICONS.SOME_OUTLINE_ICON}
 *   size={24}
 *   color="default"          // preserve the SVG's original colors
 *   backdropColor="white"    // shows through the cutouts
 *   backdropSize={14}        // smaller than 24 so it stays inside the icon
 * />
 */
export default function IconWithBackdrop(props: IconWithBackdropProps): JSX.Element {
  const {
    backdropColor,
    backdropWidth,
    backdropHeight,
    backdropSize,
    backdropOffsetX,
    backdropOffsetY,
    backdropBorderRadius,
    wrapperClassName,
    ...iconProps
  } = props;

  const fallbackSize = iconProps.size ?? (Math.max(iconProps.width ?? 0, iconProps.height ?? 0) || 18);
  const defaultBackdrop = `${Math.round(fallbackSize * 0.6)}px`;

  const style = {
    '--icon-backdrop-color': backdropColor,
    '--icon-backdrop-width': toLength(backdropWidth ?? backdropSize) ?? defaultBackdrop,
    '--icon-backdrop-height': toLength(backdropHeight ?? backdropSize) ?? defaultBackdrop,
    '--icon-backdrop-offset-x': toLength(backdropOffsetX) ?? '0px',
    '--icon-backdrop-offset-y': toLength(backdropOffsetY) ?? '0px',
    ...(backdropBorderRadius !== undefined ? { '--icon-backdrop-radius': toLength(backdropBorderRadius) } : {}),
  } as CSSProperties;

  return (
    <span className={`${styles.wrapper} ${wrapperClassName ?? ''}`} style={style}>
      <Icon {...iconProps} />
    </span>
  );
}
